'use client';

import { useState } from 'react';
import { useEvents } from '@/hooks/useEvents';
import Image from 'next/image';
import Link from 'next/link';
import { Calendar, MapPin, Heart, Sparkles, ArrowRight } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { useTranslation } from '@/translations';
import { EventCardSkeleton } from './components/EventCardSkeleton';

export default function EventsListPageClient() {
  const [searchQuery, setSearchQuery] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState<'all' | 'upcoming' | 'past'>('all');
  const { language } = useLanguage();
  const t = useTranslation(language);

  const { data, isLoading } = useEvents({
    page: 1,
    limit: 50,
    search: searchQuery,
    eventType: eventTypeFilter === 'all' ? undefined : eventTypeFilter,
    published: true,
  });

  const events = data?.data || [];

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('ro-RO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const TYPE_CHIPS: { key: 'all' | 'upcoming' | 'past'; label: string }[] = [
    { key: 'all', label: t('events.filterAll') },
    { key: 'upcoming', label: t('events.filterUpcoming') },
    { key: 'past', label: t('events.filterPast') },
  ];

  return (
    <main className="organic min-h-screen">
      {/* Hero */}
      <section
        className="relative overflow-hidden"
        style={{ padding: 'calc(64px + var(--space-8) * 1.6) clamp(20px, 4vw, 48px) var(--space-8)' }}
      >
        <div
          aria-hidden="true"
          className="absolute rounded-full"
          style={{ right: -100, top: -120, width: 280, height: 280, background: 'var(--color-accent-2-200)', zIndex: 0 }}
        />
        <div className="max-w-7xl mx-auto relative" style={{ zIndex: 1, maxWidth: 640 }}>
          <span
            className="block uppercase mb-2"
            style={{ fontSize: 13, letterSpacing: '0.06em', fontWeight: 600, color: 'var(--color-accent-700)' }}
          >
            Din atelierul nostru
          </span>
          <h1 className="mb-3" style={{ fontSize: 'clamp(34px, 4.4vw, 54px)', lineHeight: 1.08, margin: '0 0 var(--space-3)' }}>
            {t('events.title')}
          </h1>
          <p
            style={{
              fontSize: 16.5, lineHeight: 1.6,
              color: 'color-mix(in srgb, var(--color-text) 78%, transparent)',
              maxWidth: '52ch', margin: 0,
            }}
          >
            {t('events.subtitle')}
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto" style={{ padding: '0 clamp(20px, 4vw, 48px) calc(var(--space-8) * 2)' }}>
        {/* Search + type chips */}
        <div
          className="flex items-center justify-between flex-wrap gap-3"
          style={{ marginBottom: 'var(--space-6)' }}
        >
          <input
            type="text"
            className="input"
            style={{ width: 'min(360px, 100%)' }}
            placeholder={t('events.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <div className="flex" style={{ gap: 'var(--space-2)' }}>
            {TYPE_CHIPS.map((chip) => (
              <button
                key={chip.key}
                type="button"
                className={`btn ${eventTypeFilter === chip.key ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setEventTypeFilter(chip.key)}
              >
                {chip.label}
              </button>
            ))}
          </div>
        </div>

        {isLoading ? (
          <div
            className="grid"
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 'var(--space-6)' }}
          >
            {Array.from({ length: 6 }).map((_, i) => (
              <EventCardSkeleton key={i} />
            ))}
          </div>
        ) : events.length > 0 ? (
          <div
            className="grid"
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 'var(--space-6)' }}
          >
            {events.map((event) => (
              <Link
                key={event.eventId}
                href={`/events/${event.eventId}`}
                className="card elev-sm"
                style={{ padding: 0, overflow: 'hidden' }}
              >
                <div className="relative" style={{ aspectRatio: '16 / 10' }}>
                  {event.media && event.media.length > 0 && event.media[0].type === 'image' ? (
                    <Image
                      src={event.media[0].url}
                      alt={event.title}
                      fill
                      className="washed object-cover"
                      style={{ borderRadius: 0 }}
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full" style={{ background: 'var(--color-neutral-100)' }}>
                      <Calendar className="h-16 w-16" style={{ color: 'var(--color-neutral-500)' }} />
                    </div>
                  )}
                  <div
                    className="absolute flex flex-col items-end"
                    style={{ top: 12, right: 12, gap: 6 }}
                  >
                    {event.featured && (
                      <span className="tag tag-accent flex items-center gap-1">
                        <Sparkles className="h-2.5 w-2.5" strokeWidth={2.75} />
                        {t('events.featured')}
                      </span>
                    )}
                    <span className={`tag ${event.eventType === 'upcoming' ? 'tag-accent-2' : 'tag-neutral'}`}>
                      {event.eventType === 'upcoming' ? t('events.upcoming') : t('events.past')}
                    </span>
                  </div>
                </div>

                <div style={{ padding: 'var(--space-4)' }}>
                  <p className="card-title" style={{ margin: '0 0 var(--space-2)' }}>{event.title}</p>

                  <div
                    className="flex flex-col"
                    style={{ gap: 4, fontSize: 13, color: 'color-mix(in srgb, var(--color-text) 65%, transparent)', marginBottom: 'var(--space-2)' }}
                  >
                    <span className="flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5" strokeWidth={2.75} />
                      {formatDate(event.eventDate)}
                    </span>
                    {event.location && (
                      <span className="flex items-center gap-1.5">
                        <MapPin className="h-3.5 w-3.5" strokeWidth={2.75} />
                        <span className="line-clamp-1">{event.location}</span>
                      </span>
                    )}
                  </div>

                  <p
                    className="line-clamp-3"
                    style={{ fontSize: 14, lineHeight: 1.5, color: 'color-mix(in srgb, var(--color-text) 75%, transparent)', margin: '0 0 var(--space-3)' }}
                  >
                    {event.description}
                  </p>

                  <div
                    className="flex items-center justify-between"
                    style={{ paddingTop: 'var(--space-2)', borderTop: '1px solid var(--color-divider)' }}
                  >
                    <span
                      className="flex items-center gap-1.5"
                      style={{ fontSize: 13, color: 'color-mix(in srgb, var(--color-text) 60%, transparent)' }}
                    >
                      <Heart className="h-3.5 w-3.5" strokeWidth={2.75} />
                      {event.likes || 0}
                    </span>
                    <span
                      className="inline-flex items-center"
                      style={{ gap: 6, fontWeight: 600, fontSize: 14, color: 'var(--color-accent)' }}
                    >
                      Detalii
                      <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.75} />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center" style={{ padding: 'calc(var(--space-8) * 2) 0' }}>
            <h3 style={{ margin: '0 0 var(--space-2)' }}>{t('events.noEvents')}</h3>
            <p style={{ color: 'color-mix(in srgb, var(--color-text) 65%, transparent)' }}>
              {searchQuery ? t('events.tryAdjusting') : t('events.noEventsText')}
            </p>
          </div>
        )}
      </div>
    </main>
  );
}
